'use client';

import { memo, useCallback, useMemo } from 'react';
import {
	PERP_MARKETS_COLUMN_HEADERS_CONFIG,
	SPOT_MARKETS_COLUMN_HEADERS_CONFIG,
} from 'src/components/MarketSelector/MarketSelectorTable/consts';
import {
	MarketDisplayData,
	MarketSelectorTableHeaderValue,
} from 'src/components/MarketSelector/MarketSelectorTable/types';
import { getGridClasses } from 'src/components/MarketSelector/MarketSelectorTable/utils';
import { MarketCategory } from 'src/constants/markets';
import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';
import useFavouriteMarkets from 'src/hooks/useFavouriteMarkets';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { cm, cj } from 'src/utils/classNames';
import { ENUM_UTILS, UIMarket } from '@velocity-exchange/common';
import { MarketType } from '@velocity-exchange/sdk';
import MarketSelectorTableRow from 'src/components/MarketSelector/MarketSelectorTable/MarketSelectorTableRow';
import MarketSelectorTableHeader from 'src/components/MarketSelector/MarketSelectorTable/MarketSelectorTableHeader';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import { FundingRateUnit } from 'src/components/MarketStatsBar/FundingRateCard';

interface Props {
	markets: MarketDisplayData[];
	marketType: MarketType;
	onClose?: () => void;
	toggleSort: (header: MarketSelectorTableHeaderValue | undefined) => void;
	sort?: { header: MarketSelectorTableHeaderValue; isDescending: boolean };
	marketCategory: MarketCategory;
	isFavoritesSticky: boolean;
	toggleIsFavoritesSticky: () => void;
}

const MarketSelectorTable = ({
	markets,
	marketType,
	onClose,
	toggleSort,
	sort,
	marketCategory,
	isFavoritesSticky,
	toggleIsFavoritesSticky,
}: Props) => {
	const currentMarketIsPerp = ENUM_UTILS.match(marketType, MarketType.PERP);

	const { switchMarket } = useSwitchMarket();
	const selectedMarket = useDriftStore((s) => s.selectedMarket.current);
	const shownSpotMarkets = useFeatureFlagStore(
		(s) => s.flags.SHOWN_SPOT_MARKETS.markets
	);
	const isShownSpotMarketsEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('SHOWN_SPOT_MARKETS')
	);
	const { toggleFavourite } = useFavouriteMarkets();

	const [fundingRateUnit, setFundingRateUnit] =
		useSyncLocalStorage<FundingRateUnit>('fundingRateUnitPreference', 'h');
	const fundingMultiplier = fundingRateUnit === 'y' ? 24 * 365.25 : 1;

	const headersConfig = currentMarketIsPerp
		? PERP_MARKETS_COLUMN_HEADERS_CONFIG
		: SPOT_MARKETS_COLUMN_HEADERS_CONFIG;

	const marketsToDisplay = useMemo(() => {
		return markets.filter((market) => {
			if (market.market.isSpot) {
				// If SHOWN_SPOT_MARKETS is enabled, only show markets in that list
				if (isShownSpotMarketsEnabled) {
					return shownSpotMarkets.includes(market.symbol);
				}
				// If disabled, show all spot markets
				return true;
			}

			return market.market.isPerp;
		});
	}, [markets, shownSpotMarkets, isShownSpotMarketsEnabled]);

	const onSelectMarket = useCallback(
		(uiMarket: UIMarket) => {
			switchMarket({
				marketIndex: uiMarket.marketIndex,
				marketType: uiMarket.marketType,
			});

			onClose?.();
		},
		[onClose, switchMarket]
	);

	const toggleFundingRateUnit = useCallback(() => {
		setFundingRateUnit(fundingRateUnit === 'h' ? 'y' : 'h');
	}, [fundingRateUnit, setFundingRateUnit]);

	const isMobileScreenSize = useIsMobileScreenSize();

	return (
		<div className="flex flex-col">
			{/** Header */}
			<div
				className={cm(
					getGridClasses(currentMarketIsPerp),
					// `pl-13px` for better alignment of the favorite toggle icon.
					'sticky z-30 bg-container-bg pt-3 pr-2 pb-2 pl-[13px] text-text-tertiary'
				)}
			>
				{headersConfig.map((header) => (
					<MarketSelectorTableHeader
						key={`${header.value}-${header.breakpoint}`}
						header={header}
						marketCategory={marketCategory}
						currentMarketIsPerp={currentMarketIsPerp}
						toggleSort={toggleSort}
						sort={sort}
						fundingRateUnit={fundingRateUnit}
						toggleFundingRateUnit={toggleFundingRateUnit}
						isFavoritesSticky={isFavoritesSticky}
						toggleIsFavoritesSticky={toggleIsFavoritesSticky}
					/>
				))}
			</div>

			<div
				className={cj(
					'relative hidden-scroll flex max-h-[70vh] flex-col overflow-y-auto sm:h-[448px]',
					// Account for bottom bars on mobile.
					isMobileScreenSize && 'pb-28'
				)}
			>
				{markets.length === 0 && (
					<span className="flex w-full grow items-center justify-center typo-body-sm text-text-secondary">
						No markets found
					</span>
				)}

				{marketsToDisplay.map((market) => (
					<MarketSelectorTableRow
						onSelect={onSelectMarket}
						key={market.symbol}
						market={market}
						currentMarketIsPerp={currentMarketIsPerp}
						fundingMultiplier={fundingMultiplier}
						fundingRateUnit={fundingRateUnit}
						toggleFavourite={toggleFavourite}
						selected={selectedMarket?.equals(market.market) ?? false}
						marketCategory={marketCategory}
					/>
				))}
			</div>
		</div>
	);
};

export default memo(MarketSelectorTable);
